import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { clientApi } from '../../services/api';

interface MessageData {
  id: string;
  content: string;
  senderName?: string;
  senderEmail?: string;
  createdAt: string;
  expiresAt: string;
  readsRemaining: number | null;
  attachments: Array<{
    id: string;
    filename: string;
    mimeType: string;
    fileSize: number;
  }>;
}

type SmsStep = 'idle' | 'sending' | 'code' | 'verified';

export default function MessagePage() {
  const { token } = useParams<{ token: string }>();
  const [message, setMessage] = useState<MessageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);

  // SMS verification state
  const [smsRequired, setSmsRequired] = useState(false);
  const [smsStep, setSmsStep] = useState<SmsStep>('idle');
  const [phoneMasked, setPhoneMasked] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  useEffect(() => {
    loadMessage();
    checkSmsStatus();
  }, [token]);

  const checkSmsStatus = async () => {
    try {
      const response = await clientApi.getSmsStatus();
      setSmsRequired(response.data.enabled);
    } catch (err) {
      // If SMS check fails, continue without SMS verification
      setSmsRequired(false);
    }
  };

  const loadMessage = async () => {
    if (!token) {
      setError('Token manquant');
      setIsLoading(false);
      return;
    }

    try {
      const response = await clientApi.getMessage(token);
      setMessage(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Ce message n\'existe pas ou a expiré.');
      } else if (err.response?.status === 410) {
        setError('Ce message a déjà été lu le nombre maximum de fois autorisé.');
      } else {
        setError('Impossible de charger le message. Veuillez réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartReveal = () => {
    if (smsRequired) {
      handleSendCode();
    } else {
      handleReveal();
    }
  };

  const handleSendCode = async () => {
    if (!token) return;

    setIsSendingCode(true);
    setSmsStep('sending');
    try {
      const response = await clientApi.sendSmsCode(token);
      setPhoneMasked(response.data.phoneMasked || '••••••••');
      toast.success('Code de verification envoye');
      setSmsStep('code');
    } catch (err: any) {
      setSmsStep('idle');
      toast.error(err.response?.data?.error || 'Erreur lors de l\'envoi du code');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!token || !verificationCode.trim()) {
      toast.error('Veuillez entrer le code de verification');
      return;
    }

    setIsVerifyingCode(true);
    try {
      await clientApi.verifySmsCode(token, verificationCode.trim());
      setSmsStep('verified');
      toast.success('Code verifie');
      // Proceed to reveal the message
      handleReveal();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Code invalide');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleReveal = async () => {
    if (!token) return;

    setIsRevealing(true);
    try {
      await clientApi.markAsRead(token);
      setIsRevealed(true);
    } catch (err) {
      toast.error('Erreur lors de la révélation du message');
    } finally {
      setIsRevealing(false);
    }
  };

  const handleDownload = async (attachmentId: string, filename: string) => {
    if (!token) return;

    try {
      const response = await clientApi.downloadAttachment(token, attachmentId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Téléchargement démarré');
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner spinner-lg mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement du message sécurisé...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Message non disponible</h2>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!message) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/logo-black.png"
            alt="PowerITI"
            className="h-12 mx-auto mb-4 dark:hidden"
          />
          <img
            src="/images/logo-white.png"
            alt="PowerITI"
            className="h-12 mx-auto mb-4 hidden dark:block"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Message sécurisé</h1>
          {message.senderName && (
            <p className="text-gray-600 dark:text-gray-400">
              De : {message.senderName}
              {message.senderEmail && ` (${message.senderEmail})`}
            </p>
          )}
        </div>

        {/* Meta info */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <div className="badge badge-info">
            Envoyé le {formatDate(message.createdAt)}
          </div>
          <div className="badge badge-warning">
            Expire le {formatDate(message.expiresAt)}
          </div>
          {message.readsRemaining !== null && (
            <div className="badge badge-neutral">
              {message.readsRemaining} lecture{message.readsRemaining > 1 ? 's' : ''} restante{message.readsRemaining > 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Message card */}
        <div className="secure-card">
          {!isRevealed ? (
            /* Reveal prompt or SMS verification */
            <div className="text-center py-8">
              {smsStep === 'idle' && (
                <>
                  <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Message chiffré
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Ce message est chiffré et n'a pas encore été révélé.
                    <br />
                    <span className="text-sm text-yellow-600 dark:text-yellow-400">
                      Attention : cette action sera enregistrée et décomptera une lecture.
                    </span>
                  </p>
                  <button
                    onClick={handleStartReveal}
                    disabled={isRevealing}
                    className="btn-primary"
                  >
                    {isRevealing ? (
                      <span className="flex items-center">
                        <span className="spinner mr-2" />
                        Déchiffrement...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                        Révéler le message
                      </span>
                    )}
                  </button>
                </>
              )}

              {smsStep === 'sending' && (
                <>
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Verification par SMS
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Envoi du code de verification en cours...
                  </p>
                  <div className="flex justify-center">
                    <span className="spinner" />
                  </div>
                </>
              )}

              {smsStep === 'code' && (
                <>
                  <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Entrez le code
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Un code a 6 chiffres a ete envoye au {phoneMasked}
                  </p>
                  <div className="max-w-xs mx-auto mb-4">
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="input text-center text-2xl font-mono tracking-widest"
                      maxLength={6}
                      disabled={isVerifyingCode}
                    />
                  </div>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => {
                        setSmsStep('idle');
                        setVerificationCode('');
                      }}
                      className="btn-secondary"
                      disabled={isVerifyingCode}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleVerifyCode}
                      disabled={isVerifyingCode || verificationCode.length !== 6}
                      className="btn-primary"
                    >
                      {isVerifyingCode ? (
                        <span className="flex items-center">
                          <span className="spinner mr-2" />
                          Verification...
                        </span>
                      ) : (
                        'Verifier'
                      )}
                    </button>
                  </div>
                  <button
                    onClick={handleSendCode}
                    disabled={isSendingCode}
                    className="text-sm text-primary-500 hover:text-primary-600 mt-4"
                  >
                    Renvoyer le code
                  </button>
                </>
              )}
            </div>
          ) : (
            /* Revealed message */
            <div className="animate-fadeIn">
              <div className="message-content prose max-w-none dark:prose-invert">
                <p className="whitespace-pre-wrap text-gray-900 dark:text-gray-100">{message.content}</p>
              </div>

              {/* Attachments */}
              {message.attachments.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Pièces jointes ({message.attachments.length})
                  </h4>
                  <div className="space-y-2">
                    {message.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{attachment.filename}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(attachment.fileSize)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(attachment.id, attachment.filename)}
                          className="btn-outline text-sm py-1 px-3"
                        >
                          Télécharger
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Watermark */}
        <div className="watermark">
          PowerITI - Confidentiel
        </div>
      </div>
    </div>
  );
}
